/**
 * WHAT happens when the visitor sends a message while a turn is already
 * streaming — the decision AND the queue behind `App.tsx`'s `enqueueOrSend`,
 * kept as a pure module so both are testable with no React, no store and no
 * network.
 *
 * **The rule changed twice.** The first cut queued every busy send; on
 * 2026-09-16 it became GPT-Live «перебивание» (a new message interrupted the
 * turn). **On 2026-10-18 the owner cancelled that**: «Запретить писать
 * сообщение пока выполняется генерация» (INV-PH-137). Which of the things below
 * happens depends on WHAT is streaming, which `App.tsx` tracks in one ref
 * (`turnKind`, set by `dispatch` vs `runReportTurn`) because both share the
 * same `chatStore` `status` field:
 *
 * - the chat is **idle** → `'dispatch'`, sent at once, unchanged.
 * - the streaming turn is the **visitor's own** → `'ignore'` — the message is
 *   NOT taken (`enqueueOrSend` returns `false`, `lib/send.ts`'s «не принято»):
 *   the turn goes on, nothing is queued, nothing is cleared. The composer, the
 *   chips and the card buttons are inactive for the same span (`turnLock.ts`),
 *   so this is the safety net behind a disabled control, not the only guard.
 *   «Стоп» is the one explicit way out and is unchanged.
 * - the streaming turn is a `peterhof_live` **report run** → `'queue'` — its
 *   result must not be lost (the backend marks the job `reported` the moment
 *   the run starts, so an abort here would drop it for good): the message is
 *   pushed as the visitor's bubble at once (a pending mark the caller clears
 *   once it is actually sent) and dispatched the instant the report ends,
 *   ahead of any OTHER deferred report (`chat/reportScheduler.ts`'s own
 *   `cancelDeferred` still runs at the top of every real dispatch — a queued
 *   visitor message wins, the backend folds any pending brief into the turn
 *   it starts). A report run is background work the visitor did not start, so
 *   the lock does not apply to it.
 *
 * The queue itself stays a plain FIFO: one entry is the ordinary case, and a
 * second arriving while the first still waits joins it in order, so `App.tsx`
 * never has to choose between two typed questions.
 */

/** Which turn is currently streaming, tracked by `App.tsx` — `null` while the
 *  chat is idle. Shares one `chatStore.status` field with the visitor's own
 *  turns, which is exactly why the two need telling apart here. */
export type TurnKind = 'visitor' | 'report' | null;

export type SendDecision = 'dispatch' | 'ignore' | 'queue';

/**
 * `status !== 'idle'` alone cannot say what to do — a visitor's own turn and
 * a `peterhof_live` report both leave it non-idle. `turnKind` is `App.tsx`'s
 * own answer to which one it is; `null` while busy is defensive only (it
 * should not happen — `dispatch`/`runReportTurn` both set it before their
 * first `await`) and reads as `'ignore'`: a message wrongly refused costs the
 * visitor one more tap, a message wrongly queued would wait for a turn nobody
 * can see.
 */
export function decideSend(status: string, turnKind: TurnKind): SendDecision {
  if (status === 'idle') return 'dispatch';
  return turnKind === 'report' ? 'queue' : 'ignore';
}

/** The composer, the chips and the card buttons are inactive exactly when a send would be ignored. */
export function isSendLocked(status: string, turnKind: TurnKind): boolean {
  return decideSend(status, turnKind) === 'ignore';
}

/** A message that was pushed to the thread already but not sent yet. */
export type QueuedSend = {
  text: string;
  /** The bubble `pushUser` created for it, so the caller can mark it sent
   *  (clear the pending mark, restamp its `turn`) without pushing a second
   *  bubble for the same message. */
  bubbleId: string;
  /** The board tap this message carries, if any — captured at ENQUEUE time
   *  (`a2ui/actions.ts`'s `takeLastTap`, a one-shot read), since by the time
   *  this message is actually dispatched a later tap or typed message may
   *  already have consumed or overwritten it. */
  tap: Record<string, unknown> | null;
};

export type SendQueueState = {
  queue: QueuedSend[];
};

export function createSendQueueState(): SendQueueState {
  return { queue: [] };
}

/** Appends a message to the end of the queue. */
export function enqueue(state: SendQueueState, item: QueuedSend): void {
  state.queue.push(item);
}

/** Takes the oldest queued message, or `undefined` with nothing waiting. */
export function dequeue(state: SendQueueState): QueuedSend | undefined {
  return state.queue.shift();
}

export function hasQueued(state: SendQueueState): boolean {
  return state.queue.length > 0;
}

/** «Новый диалог» — a fresh thread owes nothing to the old one's queue. */
export function resetSendQueue(state: SendQueueState): void {
  state.queue.length = 0;
}
